import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { LightbulbIcon, SparklesIcon } from 'lucide-react';

interface AnalysisDetailDialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Sits in the badge beside the title. Defaults to the sparkle the AI overview uses. */
  icon?: ReactNode;
  /** The claim the reader should leave with, set large on the tinted block. */
  insight?: ReactNode;
  children: ReactNode;
}

/**
 * The read-more dialog: one claim on a tinted block, then the detail behind it.
 *
 * The design replaces the default close X with a labelled action, so the way back to the
 * summary the reader came from is explicit rather than an icon they have to interpret.
 */
export const AnalysisDetailDialog = ({
  open,
  onClose,
  title,
  icon,
  insight,
  children,
}: AnalysisDetailDialogProps) => {
  const { t } = useTranslation('analysis');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[760px]" showCloseButton={false}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="flex flex-1 items-center gap-3">
              <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-primary">
                {icon ?? <SparklesIcon className="size-[17px]" />}
              </span>
              {title}
            </DialogTitle>
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                {t('analysisResult.mediaLandscape.backToSummary')}
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {insight && (
            <div className="flex flex-col gap-1.5 rounded-lg bg-accent px-5 py-4">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-accent-foreground">
                <LightbulbIcon className="size-3.5" />
                {t('analysisResult.mediaLandscape.keyInsight')}
              </span>
              <p className="text-pretty text-2xl font-medium leading-8 -tracking-[0.01em] text-foreground">
                {insight}
              </p>
            </div>
          )}
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** The bulleted body the AI overview fills the dialog with. */
export const AnalysisDetailBullets = ({ items }: { items: string[] }) => (
  <ul className="flex flex-col gap-4">
    {items.map((item, position) => (
      <li
        key={`${position}-${item}`}
        className="flex gap-3 text-base leading-6 text-foreground-alt"
      >
        <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
        <span className="text-pretty">{item}</span>
      </li>
    ))}
  </ul>
);
